import { POINTER_CLASSES } from "../../config/config";
import InteractiveAnnotation from "./interactiveAnnotation";
import { drawBox, setFontStyle, measureText, hexToRgb } from "../canvasUtils";
import { isInBoundsBox, segmentLengthPoints } from "../geometry";
import store from "../../store";
export default class Text extends InteractiveAnnotation {
  constructor(position, options) {
    super(position, options);

    this.topLeftX = position.x;
    this.topLeftY = position.y;
    this.contactPoint = { x: 0, y: 0 };
    this.distanceFromTopLeft = { x: 0, y: 0 };
    this.clickDetected = false;

    let size = options.textSize || 40;
    this.size = size;
    this.font = size + "px Calibri";
    this.baseline = "hanging";
    let rgb = hexToRgb(options.color);
    this.backgroundColor = `rgba(${rgb.r},${rgb.g},${rgb.b},0.5)`;
    let avg = (rgb.r + rgb.g + rgb.b) / 3;
    if (avg < 128) this.fontColor = "#D9D9D9";
    else this.fontColor = "#595959";

    this.activeMovement = false;
    this.activeCorner = false;

    this.minWidth = Math.min(size * 12, 300);
    this.width = this.minWidth;
    this.height = size + size / 5;
    this.lineHeight = size + size / 5;
    this.minHeight = size + size / 5;

    this.text = "";
    this.rows = [{ text: "", textWidth: 0, start: 0, end: 0, endsWithLineBreak: false }];
    this.showBackgroundBox = true;

    this.syncPair = null;
  }

  get isActive() {
    return this.activeMovement || !!this.activeCorner;
  }

  onCreate(){
    this.openDialog();
  }

  openDialog(){
    store.commit("setTextAnnotationToEdit", this);
  }

  draw(ctx) {
    if (this.showBackgroundBox) this.drawBackground(ctx);
    if (this.isActive) this.drawInteractionBoxes(ctx);
    if (this.text) this.drawText(ctx);
  }

  drawBackground(ctx) {
    ctx.beginPath();
    ctx.fillStyle = this.backgroundColor;
    ctx.fillRect(this.topLeftX, this.topLeftY, this.width, this.height);
    ctx.closePath();
  }

  drawText(ctx) {
    setFontStyle(ctx, this);
    for (let i = 0; i < this.rows.length; i++) this.drawRow(ctx, i);
  }

  drawRow(ctx, rowIndex) {
    let row = this.rows[rowIndex];
    let x = this.topLeftX;
    let y = this.topLeftY + rowIndex * this.lineHeight;
    ctx.fillStyle = this.fontColor;
    ctx.fillText(row.text, x, y);
  }

  drawInteractionBoxes(ctx) {
    drawBox(ctx, this.topLeftX, this.topLeftY, this.transform.zoom, 6);
    drawBox(ctx, this.topLeftX, this.bottomRightY, this.transform.zoom, 6);
    drawBox(ctx, this.bottomRightX, this.topLeftY, this.transform.zoom, 6);
    drawBox(ctx, this.bottomRightX, this.bottomRightY, this.transform.zoom, 6);
  }

  get bottomRightX() {
    return this.topLeftX + this.width;
  }

  get bottomRightY() {
    return this.topLeftY + this.height;
  }

  get topLeft() {
    return { x: this.topLeftX, y: this.topLeftY };
  }

  get topRight() {
    return { x: this.bottomRightX, y: this.topLeftY };
  }

  get bottomLeft() {
    return { x: this.topLeftX, y: this.bottomRightY };
  }

  get bottomRight() {
    return { x: this.bottomRightX, y: this.bottomRightY };
  }

  isInBounds(x, y) {
    return this.isInBoundsResizeCorner(x, y)
      || this.isInBoundsTextBox(x, y);
  }

  isInBoundsResizeCorner(x, y) {
    return this.isInBoundsCorner(x, y, this.topLeft) 
      || this.isInBoundsCorner(x, y, this.topRight)
      || this.isInBoundsCorner(x, y, this.bottomRight)
      || this.isInBoundsCorner(x, y, this.bottomLeft);
  }

  isInBoundsCorner(x, y, { x: cx, y: cy }) {
    return isInBoundsBox(x, y, cx, cy, this.transform.zoom);
  }

  isInBoundsTextBox(x, y) {
    return x > this.topLeftX
      && x < this.bottomRightX
      && y > this.topLeftY
      && y < this.bottomRightY;
  }

  mousedown(x, y) {
    this.clearActiveStates();
    if (this.isInBoundsCorner(x, y, this.topRight, 6)) {
      this.activeCorner = "TR";
    } else if (this.isInBoundsCorner(x, y, this.bottomRight, 6)) {
      this.activeCorner = "BR";
    } else if (this.isInBoundsCorner(x, y, this.bottomLeft, 6)) {
      this.activeCorner = "BL";
    } else if (this.isInBoundsCorner(x, y, this.topLeft, 6)) {
      this.activeCorner = "TL";
    } else {
      this.contactPoint.x = x;
      this.contactPoint.y = y;
      this.distanceFromTopLeft.x = this.contactPoint.x - this.topLeftX;
      this.distanceFromTopLeft.y = this.contactPoint.y - this.topLeftY;
      this.activeMovement = true;
      this.clickDetected = true;
    }

    this.signalReady();
  }

  mousemove(x, y) {
    if (this.activeMovement) this.updateLocation(x, y);
    else if (this.activeCorner) this.updateSize(x, y);
    this.signalReady();
  }

  mouseup() {
     if (this.clickDetected)
      this.openDialog();
    
    this.clickDetected = false;
    this.contactPoint.x = 0;
    this.contactPoint.y = 0;
    this.distanceFromTopLeft.x = 0;
    this.distanceFromTopLeft.y = 0;
    this.signalReady();
  }

  updateLocation(x, y) {
    this.topLeftX = x - this.distanceFromTopLeft.x;
    this.topLeftY = y - this.distanceFromTopLeft.y;
    this.updateClickDetection(x, y);
  }

  updateClickDetection(x, y){
    if (!this.clickDetected) return;
    let delta = 5;
    let distance = segmentLengthPoints({x, y}, this.contactPoint);
    this.clickDetected = distance < delta;
  }

  updateSize(x, y) {
    let width = 0;
    let height = 0;

    if (this.activeCorner.includes("L")) {
      width = Math.max(this.bottomRightX - x, this.minWidth);
      if (width > this.minWidth) this.topLeftX = x;
    }
    else if (this.activeCorner.includes("R")) width = Math.max(x - this.topLeftX, this.minWidth);

    const doReSplit = (width !== this.width);
    this.width = width;
    if (doReSplit)
      this.splitLinesAndSetMinHeight();


    if (this.activeCorner.includes("B")) height = Math.max(y - this.topLeftY, this.minHeight);

    if (this.activeCorner.includes("T")) {
      height = Math.max(this.bottomRightY - y, this.minHeight);
      if (height > this.minHeight) this.topLeftY = y;
    }

    this.height = height;
  }

  deactivate() {
    this.clearActiveStates();
    this.signalReady();
  }

  clearActiveStates() {
    this.activeMovement = false;
    this.activeCorner = null;
    this.clickDetected = false;
    this.contactPoint.x = 0;
    this.contactPoint.y = 0;
    this.distanceFromTopLeft.x = 0;
    this.distanceFromTopLeft.y = 0;
  }

  getRowIndex(offsetY) {
    let rowIndex = Math.floor(offsetY / this.lineHeight);
    return Math.max(0, Math.min(this.rows.length - 1, rowIndex));
  }

  setSelectionIndices(selection, index, row, col) {
    selection.index = index;
    selection.row = row;
    selection.col = col;
  }

  getClosestColumn(text, x) {
    // we want the closest index on the left
    let length = 0;
    for (let i = 0; i < text.length; i++) {
      let currentChar = text[i];
      let width = measureText(currentChar, this.font);
      if (x < (length + width / 2)) return i;
      length += width;
    }
    return text.length;
  }

  flipSelectionEndpoints() {
    let start = Object.assign({}, this.selectionStart);
    this.selectionStart = this.selectionEnd;
    this.selectionEnd = start;
    this.selectionDirection = this.selectionDirection == "forward" ? "backward" : "forward";
  }

  getHoverClass(x, y) {
    if (this.isInBoundsCorner(x, y, this.topLeft)) return POINTER_CLASSES.nwseResize;
    if (this.isInBoundsCorner(x, y, this.topRight)) return POINTER_CLASSES.neswResize;
    if (this.isInBoundsCorner(x, y, this.bottomLeft)) return POINTER_CLASSES.neswResize;
    if (this.isInBoundsCorner(x, y, this.bottomRight)) return POINTER_CLASSES.nwseResize;
    return POINTER_CLASSES.text;
  }

  splitLinesAndSetMinHeight() {
    this.measureTextAndSplitLines();
    this.increaseHeightIfNeeded();
  }

  // this.rows = [{ text: "", textWidth: 0, start: 0, end: 0, endsWithLineBreak: false }];
  measureTextAndSplitLines() {
    this.rows = [];
    let start = 0;
    let textWidth = 0;
    let text = "";
    for (let i = 0; i < this.text.length; i++) {
      let currentLetter = this.text.charAt(i);
      let lineBreak = this.isLineBreak(currentLetter);
      let charWidth = 0;

      if (!lineBreak) {
        charWidth = measureText(currentLetter, this.font);
        textWidth += charWidth;
        text += currentLetter;
      }

      let maxWidth = this.width - charWidth;
      if (lineBreak || textWidth > maxWidth) {
        this.rows.push({ text, textWidth, start, end: i + 1, endsWithLineBreak: lineBreak });
        start = i + 1;
        textWidth = 0;
        text = "";
      }
    }
    // put whatever's leftover at the end
    let leftover = this.text.substring(start);
    let leftoverWidth = measureText(leftover, this.font);
    let lastChar = this.text.charAt(this.text.length - 1);
    if (!this.rows.length || leftover || this.isLineBreak(lastChar))
      this.rows.push({ text: leftover, textWidth: leftoverWidth, start, end: this.text.length, endsWithLineBreak: false });
  }

  isLineBreak(letter) {
    return letter === "\n" || letter === "\r";
  }

  increaseHeightIfNeeded() {
    this.minHeight = this.getMinHeightRequired();
    this.height = Math.max(this.height, this.minHeight);
  }

  getMinHeightRequired() {
    return this.rows.length * this.lineHeight;
  }

  updateTextArea(text) {
    this.text = text;
    this.splitLinesAndSetMinHeight();
    this.signalReady();
  }

  duplicateAndSyncAnnotation(e) {
    this.updateTextArea(e.text);
    this.width = e.width;
    this.height = e.height;
    e.setSyncPair(this);
    this.setSyncPair(e);
  }

  setSyncPair(obj) {
    this.syncPair = obj;
  }

  syncText() {
    if (this.syncPair) {
      this.syncPair.updateTextSize(this.size);
      this.syncPair.updateTextArea(this.text);
      this.syncPair.topLeftX = this.topLeftX;
      this.syncPair.topLeftY = this.topLeftY;
      this.syncPair.minWidth = this.minWidth;
      this.syncPair.width = this.width;
      this.syncPair.height = this.height;
      this.syncPair.lineHeight = this.lineHeight;
      this.syncPair.minHeight = this.minHeight;
    }
  }

  updateTextSize(size){
    size = Number(size);
    if (size == this.size) return;
    this.size = size || 40;
    this.font = size + "px Calibri";
    this.minWidth = Math.min(size * 12, 600);
    this.width = Math.max(this.width, this.minWidth);
    this.lineHeight = size + size / 5;
    this.splitLinesAndSetMinHeight();
    this.height = this.minHeight;
    this.signalReady();
  }

  setWidthToText() {
    let maxRowSize = 0;
    for (let j = 0; j < this.rows.length; j++) 
    {
      if (this.rows[j].textWidth > maxRowSize)
        maxRowSize = this.rows[j].textWidth;
    }
    this.minWidth = maxRowSize;
    this.width = this.minWidth;
  }

  setShowBackgroundBox(val) {
    this.showBackgroundBox = val.value;
  }

  updateFontColor(val) {
    this.fontColor = val;
  }
}